import { useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { LayoutList, FileSpreadsheet } from "lucide-react";
import {
	Table as UITable,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
	TableCell,
	Skeleton,
} from "@hotelise/ui";

import { cn } from "utils/cn";
import { StatementListItem } from "components/molecules";

import { Statement } from "interfaces/statements";

const DEFAULT_COLUMNS = [
	{ id: "statementNumber", label: "Statement #", isVisible: true, width: "1fr" },
	{ id: "property", label: "Property", isVisible: true, width: "1fr" },
	{ id: "period", label: "Period", isVisible: true, width: "1fr" },
	{ id: "reservations", label: "Reservations", isVisible: true, width: "1fr" },
	{ id: "income", label: "Income", isVisible: true, width: "1fr" },
	{ id: "verified", label: "Draft/Verified", isVisible: true, width: "0.6fr" },
	{ id: "viewInvoice", label: "View Statement", isVisible: true, width: "0.8fr" },
];

const SKEL_ROWS = ["a", "b", "c", "d", "e", "f"];
const SKEL_MOBILE_CARDS = ["a", "b", "c", "d", "e"];

interface Props {
	statements: Statement[];
	isLoading: boolean;
	hasMore: boolean;
	loadMore: () => void;
	showProperty?: boolean;
	scrollableTarget?: string;
}

const StatementsList = (props: Props): JSX.Element => {
	const { statements, isLoading, hasMore, loadMore, showProperty, scrollableTarget } = props;
	const [viewMode, setViewMode] = useState<"compact" | "detailed">("compact");

	const visibleColumns = useMemo(() => {
		return DEFAULT_COLUMNS.filter((col) => {
			if (!showProperty && col.id === "property") return false;
			return true;
		});
	}, [showProperty]);

	const visibleColumnIds = useMemo(
		() => visibleColumns.map((c) => c.id),
		[visibleColumns]
	);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	const renderLoadMoreLoader = (type: "desktop" | "mobile") => (
		<div className="py-2">
			{type === "desktop" ? (
				<UITable>
					<TableBody>
						{["lm-1", "lm-2", "lm-3"].map((rowId) => (
							<TableRow key={rowId}>
								{visibleColumnIds.map((colId) => (
									<TableCell key={`${rowId}-${colId}`}>
										<Skeleton className="h-4 w-3/4" />
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</UITable>
			) : (
				["lm-1", "lm-2"].map((id) => (
					<div key={id} className="rounded-lg border bg-card p-3 space-y-2 mt-3">
						<div className="flex items-center justify-between">
							<Skeleton className="h-4 w-28" />
							<Skeleton className="h-3 w-16" />
						</div>
						<div className="grid grid-cols-3 gap-2 rounded-md bg-muted/50 p-2">
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
							<Skeleton className="h-8 w-full" />
						</div>
						<div className="flex items-center justify-between pt-1">
							<Skeleton className="h-3 w-24" />
							<Skeleton className="h-3 w-20" />
						</div>
					</div>
				))
			)}
		</div>
	);

	const renderDesktopSkeleton = () => (
		<div className="hidden md:block rounded-md border overflow-hidden">
			<UITable>
				<TableHeader>
					<TableRow>
						{visibleColumns.map((col) => (
							<TableHead key={col.id}>
								<Skeleton className="h-4 w-full" />
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{SKEL_ROWS.map((key) => (
						<TableRow key={key}>
							{visibleColumns.map((col) => (
								<TableCell key={`${key}-${col.id}`}>
									<Skeleton className="h-4 w-3/4" />
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</UITable>
		</div>
	);

	const renderMobileSkeleton = () => (
		<div className="md:hidden space-y-3">
			{SKEL_MOBILE_CARDS.map((key) => (
				<div key={key} className="rounded-lg border bg-card p-3 space-y-2">
					<div className="flex items-center justify-between">
						<Skeleton className="h-4 w-28" />
						<Skeleton className="h-3 w-16" />
					</div>
					<div className="grid grid-cols-3 gap-2 rounded-md bg-muted/50 p-2">
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
					</div>
					<div className="flex justify-between pt-1">
						<Skeleton className="h-3 w-24" />
						<Skeleton className="h-3 w-20" />
					</div>
				</div>
			))}
		</div>
	);

	const renderDesktopTable = () => (
		<section className="hidden md:block">
			<InfiniteScroll
				scrollableTarget={scrollableTarget}
				dataLength={statements.length}
				next={loadMore}
				hasMore={hasMore}
				loader={renderLoadMoreLoader("desktop")}
			>
				<div className="rounded-md border overflow-hidden">
					<UITable>
						<TableHeader>
							<TableRow>
								{isColumnVisible("statementNumber") && <TableHead>Statement #</TableHead>}
								{showProperty && isColumnVisible("property") && <TableHead>Property</TableHead>}
								{isColumnVisible("period") && <TableHead>Period</TableHead>}
								{isColumnVisible("reservations") && <TableHead>Reservations</TableHead>}
								{isColumnVisible("income") && <TableHead>Income</TableHead>}
								{isColumnVisible("verified") && <TableHead className="text-center">Draft/Verified</TableHead>}
								{isColumnVisible("viewInvoice") && <TableHead>View Statement</TableHead>}
							</TableRow>
						</TableHeader>
						<TableBody>
							{statements.map((statement: Statement) => (
								<StatementListItem
									data={statement}
									key={statement.id}
									showProperty={showProperty}
									visibleColumnIds={visibleColumnIds}
									viewMode={viewMode}
								/>
							))}
							{statements.length === 0 && !isLoading && (
								<TableRow>
									<TableCell
										colSpan={visibleColumns.length}
										className="text-center text-sm text-muted-foreground py-8"
									>
										No statements found.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</UITable>
				</div>
			</InfiniteScroll>
		</section>
	);

	const renderMobileCards = () => (
		<section className="md:hidden">
			<InfiniteScroll
				scrollableTarget={scrollableTarget}
				dataLength={statements.length}
				next={loadMore}
				hasMore={hasMore}
				loader={renderLoadMoreLoader("mobile")}
				className="flex flex-col gap-3 !overflow-hidden"
			>
				{statements.map((statement: Statement) => (
					<StatementListItem
						data={statement}
						key={statement.id}
						showProperty={showProperty}
						isMobile
						viewMode={viewMode}
					/>
				))}
			</InfiniteScroll>
			{statements.length === 0 && !isLoading && (
				<p className="text-sm text-muted-foreground py-4">
					No statements found.
				</p>
			)}
		</section>
	);

	const renderContent = () => {
		if (isLoading && statements.length === 0) {
			return (
				<>
					{renderDesktopSkeleton()}
					{renderMobileSkeleton()}
				</>
			);
		}

		return (
			<>
				{renderDesktopTable()}
				{renderMobileCards()}
			</>
		);
	};

	const viewToggle = (
		<div className="flex items-center gap-1 p-0.5 rounded-lg bg-muted w-fit">
			<button
				className={cn(
					"flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
					viewMode === "compact"
						? "bg-background text-foreground shadow-sm"
						: "text-muted-foreground hover:text-foreground"
				)}
				onClick={() => setViewMode("compact")}
			>
				<LayoutList className="h-3.5 w-3.5" />
				Compact
			</button>
			<button
				className={cn(
					"flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
					viewMode === "detailed"
						? "bg-background text-foreground shadow-sm"
						: "text-muted-foreground hover:text-foreground"
				)}
				onClick={() => setViewMode("detailed")}
			>
				<FileSpreadsheet className="h-3.5 w-3.5" />
				Detailed
			</button>
		</div>
	);

	return (
		<div className="pt-4 pb-24 md:pb-[70px] overflow-y-auto">
			<div className="flex justify-between items-center mb-2.5">
				{viewToggle}
			</div>
			{renderContent()}
		</div>
	);
};

export default StatementsList;
